import { Store } from "vuex";
import getCacheKey from "@/vue/planning/logic/getCacheKey";
import responsesCache from "@/vue/planning/state/responsesCache";
import { getAvailRequestData } from "@/vue/planning/logic/getRequestData";
import { AvailabilitiesResponse } from "@/types/responses";
import * as _ from "lodash";
import { getUniqueId } from "@/vue/planning/logic/uniqueId";
import isHaveAvailability from "@/vue/planning/logic/isHaveAvailability";
import { checkIsSummerOrWinter } from "@/utils/utils";
import { PrestationData } from "@/types";
import { getAvailablePackagesWiyp } from "./availablesPackagesWiyp";

const syncPackAvailabilitiesToStore = (
  availResp: AvailabilitiesResponse,
  packCode: string,
  storeObj: Store<any>
): AvailabilitiesResponse => {
  // 1️⃣  Ensure every prestation has a deterministic ID
  if (!availResp.prestations)
    throw new Error("prestations are required in availResp.prestations");

  availResp.prestations = availResp.prestations.map((prest: any) => {
    prest["id"] = getUniqueId(prest);
    return prest;
  });

  // 2️⃣  Build a `{ [id]: prest }` map for the given packCode
  const newMapObj: Record<string, any> = {
    ...storeObj.state.prestMaps.seAvailabilities,
    [packCode]: {},
  };

  availResp.prestations.forEach(
    (prest: any) => (newMapObj[packCode][prest.id] = prest)
  );

  // 3️⃣  Sync both maps in the store
  storeObj.state.prestMaps.seAvailabilities = newMapObj;
  storeObj.state.prestMaps.prices = newMapObj;

  return availResp;
};

const updateDisplayedPrestations = async (storeObj: Store<any>, existingAvailabilities: AvailabilitiesResponse) => {
    const prestationsOnPage = window.lihe?.prestations || [];

    // Create a Set of prestationIds already on the page
    const pagePrestationIds = new Set(prestationsOnPage.map((p: PrestationData) => p.prestationId));

    // Filter out prestations that are already on the page
    const existingAvailabilitiesNotInPage = existingAvailabilities.prestations!.filter(
      (prest) => !pagePrestationIds.has(prest.prestIdFusion)
    );

    if(existingAvailabilitiesNotInPage.length === 0) return

    const prestFromCatalogPromises = existingAvailabilitiesNotInPage.map(prest => {
      const requestData = {
        locale: storeObj.getters.locale,
        prestationId: prest.prestId,
      };
      return window.catalogService.getPrestationByLocale(requestData); // Returns a promise
    });
    
    const results = await Promise.all(prestFromCatalogPromises);
    
    storeObj.dispatch("updateInitialPrestations", results)
}

const getAvailability = async (
  packCode: string,
  startDate: string,
  endDate: string,
  storeObj: Store<any>
): Promise<AvailabilitiesResponse | null> => {
  if (storeObj.state.datepickerStartDate) {
    const newSeason = checkIsSummerOrWinter(storeObj.state.datepickerStartDate);
    if (newSeason !== storeObj.state.season) {
      storeObj.state.season = newSeason;

      window.seasonManager.switchSeasonWithoutReloading();
    }
  }
  // try to extract cached response
  const cacheKey = getCacheKey(
    packCode,
    startDate,
    endDate,
    storeObj.state.season,
    storeObj.state.participants,
    storeObj.state.skipass
  );

  const existingAvailabilities = responsesCache.value.getResponse(
    "availabilities",
    cacheKey
  ) as AvailabilitiesResponse;
  
  if(existingAvailabilities && existingAvailabilities.prestations) {
    updateDisplayedPrestations(storeObj, existingAvailabilities)
    return syncPackAvailabilitiesToStore(existingAvailabilities, packCode, storeObj);
  }

  const requestData = getAvailRequestData(
    storeObj.state,
    storeObj.getters,
    packCode
  );

  const avail_resp: AvailabilitiesResponse =
    await window.searchEngineService.getRelatedPackages(
      requestData,
      "getAvailabilities"
    );
  storeObj.state.availablePackageCodeWiyp = getAvailablePackagesWiyp(avail_resp);

  if (!isHaveAvailability(avail_resp)) {
    return null;
  }

  const packageId = avail_resp?.availablePackages?.find((packObj: any) => {
    return packObj.codePackage === packCode;
  })?.prestId;

  storeObj.commit("setPackageId", packageId);

  if (!avail_resp.prestations)
    throw new Error(
      "No prestations was received from the `availabilities` endpoint"
    );

  syncPackAvailabilitiesToStore(avail_resp, packCode, storeObj);

  responsesCache.value.setResponse("availabilities", cacheKey, avail_resp);
  responsesCache.value = _.cloneDeep(responsesCache.value);

  return avail_resp;
};

export default getAvailability;
